/**
 * useQuestionnaireBranching — branching logic engine for questionnaire step routing.
 * Evaluates branch rules to determine conditional step navigation,
 * with cycle detection and fallback to order-based routing.
 */
import { useMemo, useCallback, useRef } from 'react';
import type { QuestionnaireStep, BranchCondition } from '@/types/pm-types';

export interface UseQuestionnaireBranchingResult {
  /** Get the next step ID based on branch rules + responses. Returns null if at the last step. */
  getNextStepId: (currentStepId: string, responses: Record<string, unknown>) => string | null;
  /** Get the previous step ID from the step path history. Returns null if at the first step. */
  getPreviousStepId: (currentStepId: string) => string | null;
  /** Get the ordered list of step IDs the user has visited. */
  getStepPath: () => string[];
  /** Check if the given step is the last reachable step based on current responses. */
  isLastStep: (stepId: string, responses: Record<string, unknown>) => boolean;
  /** Push a step ID onto the path (called internally by navigation). */
  pushStepPath: (stepId: string) => void;
  /** Pop the last step from the path (for back navigation). Returns the new current step ID. */
  popStepPath: () => string | undefined;
  /** Reset the path to the first step. */
  resetPath: (firstStepId: string) => void;
}

/** Evaluate a single branch condition against the collected responses. */
export function evaluateBranchCondition(
  condition: BranchCondition,
  responses: Record<string, unknown>,
): boolean {
  const answer = responses[condition.questionId];
  switch (condition.operator) {
    case 'equals': return answer === condition.value;
    case 'not_equals': return answer !== condition.value;
    case 'contains':
      if (Array.isArray(answer)) return answer.includes(condition.value);
      if (typeof answer === 'string') return answer.includes(String(condition.value));
      return false;
    case 'not_contains':
      if (Array.isArray(answer)) return !answer.includes(condition.value);
      if (typeof answer === 'string') return !answer.includes(String(condition.value));
      return true;
    case 'greater_than': return Number(answer) > Number(condition.value);
    case 'less_than': return Number(answer) < Number(condition.value);
    default: return false;
  }
}

/** Compute the next step ID given branch rules and responses (pure function). */
export function computeNextStepId(
  currentStepId: string,
  steps: QuestionnaireStep[],
  responses: Record<string, unknown>,
  visitedPath: string[],
): string | null {
  const currentStep = steps.find((s) => s.id === currentStepId);
  if (!currentStep) return null;

  // Evaluate branch rules in order — first match wins
  if (currentStep.branchRules) {
    for (const rule of currentStep.branchRules) {
      if (evaluateBranchCondition(rule.condition, responses)) {
        // Cycle detection: skip if target already visited
        if (!visitedPath.includes(rule.targetStepId)) {
          return rule.targetStepId;
        }
      }
    }
  }

  // Default: next step by order
  const sortedSteps = [...steps].sort((a, b) => a.order - b.order);
  const currentIdx = sortedSteps.findIndex((s) => s.id === currentStepId);
  if (currentIdx < sortedSteps.length - 1) {
    return sortedSteps[currentIdx + 1].id;
  }

  return null; // last step
}

/** Check if a step is the last reachable step (pure function). */
export function computeIsLastStep(
  stepId: string,
  steps: QuestionnaireStep[],
  responses: Record<string, unknown>,
  visitedPath: string[],
): boolean {
  return computeNextStepId(stepId, steps, responses, visitedPath) === null;
}

export function useQuestionnaireBranching(
  steps: QuestionnaireStep[],
): UseQuestionnaireBranchingResult {
  const stepPathRef = useRef<string[]>([steps[0]?.id || '']);

  // Memoize sorted steps to avoid repeated sorting
  const sortedSteps = useMemo(
    () => [...steps].sort((a, b) => a.order - b.order),
    [steps],
  );

  const getNextStepId = useCallback(
    (currentStepId: string, responses: Record<string, unknown>): string | null => {
      return computeNextStepId(currentStepId, sortedSteps, responses, stepPathRef.current);
    },
    [sortedSteps],
  );

  const getPreviousStepId = useCallback(
    (_currentStepId: string): string | null => {
      const path = stepPathRef.current;
      if (path.length <= 1) return null;
      return path[path.length - 2];
    },
    [],
  );

  const getStepPath = useCallback((): string[] => {
    return [...stepPathRef.current];
  }, []);

  const isLastStep = useCallback(
    (stepId: string, responses: Record<string, unknown>): boolean => {
      return computeIsLastStep(stepId, sortedSteps, responses, stepPathRef.current);
    },
    [sortedSteps],
  );

  const pushStepPath = useCallback((stepId: string) => {
    stepPathRef.current = [...stepPathRef.current, stepId];
  }, []);

  const popStepPath = useCallback((): string | undefined => {
    if (stepPathRef.current.length <= 1) return undefined;
    const newPath = stepPathRef.current.slice(0, -1);
    stepPathRef.current = newPath;
    return newPath[newPath.length - 1];
  }, []);

  const resetPath = useCallback((firstStepId: string) => {
    stepPathRef.current = [firstStepId];
  }, []);

  return {
    getNextStepId,
    getPreviousStepId,
    getStepPath,
    isLastStep,
    pushStepPath,
    popStepPath,
    resetPath,
  };
}
